/** Email proof first, then name completion only for incomplete accounts. */
import { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, TextField, Typography } from "@material-ui/core";
import InstitutionalOtpCodeStep from "./InstitutionalOtpCodeStep";
import {
  completeInstitutionalProfile,
  INSTITUTIONAL_CODES,
  sendLoginCode,
  verifyLoginCode,
} from "./institutional-api";
import { messageForCode, SUPPORT_EMAIL } from "./institutional-copy";
import {
  emailCodeDoorFor,
  normalizeFreeText,
  normalizeInstitutionalEmail,
  validateDisplayName,
  validateLoginEmail,
} from "./institutional-email";
import { isEmailCodeAnyAddressEnabled } from "./institutional-flags";
import { getActiveReferralCode, getReferralCapturedAtForSignup } from "../../utils/referral";
import { buildQLinkOAuthState } from "../../utils/attribution";
import { isTokenPersistable } from "../../utils/session";
import {
  getInstitutionalProfile, saveInstitutionalProfile,
  clearInstitutionalProfile, abandonInstitutionalProfile,
} from "./institutional-profile-session";
import "./institutional.scss";
import { REPLAY_MASK } from "../../utils/replay-mask";

const STEPS = {
  DETAILS: "details",
  PROFILE: "profile",
  CODE: "code",
  LINK_REQUIRED: "linkRequired",
  // Any-address email code only (SIGN_IN_ANOTHER_WAY): this address signs in another way.
  OTHER_WAY: "otherWay",
  SIGNING_IN: "signingIn",
};

// The host learns which door a sign-in used only when it is the new one, so with the
// any-address flag off (or for a school address) the hand-over call is exactly today's.
const handOver = (onSignedIn, user, email) => {
  const door = emailCodeDoorFor(email);
  if (door) {
    onSignedIn(user, { door });
  } else {
    onSignedIn(user);
  }
};

const readReferralForSignup = () => {
  // The single read path: drops a code past its 30-day capture window.
  const referralCode = getActiveReferralCode(window.localStorage);
  if (!referralCode) return {};
  // Same instant the Google and phone doors send (#830): ISO, backdated an hour so a
  // fast device clock cannot make it "future", and omitted past a week, because the
  // server drops the whole referral (the friend's reward too) for a future or stale one.
  const referralCapturedAt = getReferralCapturedAtForSignup(window.localStorage) || undefined;
  return { referralCode, referralCapturedAt };
};

const InstitutionalSignIn = ({
  onSignedIn,
  onProfilePending = () => {},
  onUseGoogle,
  onUsePhone,
  qlinkId,
  qlinkPlatform,
}) => {
  const [pendingUser, setPendingUser] = useState(getInstitutionalProfile);
  const [step, setStep] = useState(() => pendingUser ? STEPS.PROFILE : STEPS.DETAILS);
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [errorCode, setErrorCode] = useState(null);
  const [challenge, setChallenge] = useState(null);
  const [sentTo, setSentTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  // Whether the SIGN_IN_ANOTHER_WAY refusal came after the code was verified.
  const [refusedAfterProof, setRefusedAfterProof] = useState(false);
  // Requests resolve after the student may have switched tabs (unmounting this
  // surface); drop those late results instead of updating a dead component.
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const errorMessage = errorCode ? messageForCode(errorCode) : null;
  const anyAddress = isEmailCodeAnyAddressEnabled();

  const handleSend = async (event) => {
    event.preventDefault();
    if (busy) return;
    const errors = {
      email: validateLoginEmail(email),
    };
    setFieldErrors(errors);
    if (errors.email) return;

    const normalizedEmail = normalizeInstitutionalEmail(email);
    setBusy(true);
    setErrorCode(null);
    const result = await sendLoginCode(normalizedEmail);
    if (!mountedRef.current) return;
    setBusy(false);
    if (result.ok) {
      setSentTo(normalizedEmail);
      setChallenge(result.challenge);
      setStep(STEPS.CODE);
      return;
    }
    if (result.code === INSTITUTIONAL_CODES.INVALID_INSTITUTIONAL_EMAIL) {
      setFieldErrors({ ...errors, email: messageForCode(result.code) });
      return;
    }
    if (result.code === INSTITUTIONAL_CODES.SIGN_IN_ANOTHER_WAY) {
      setSentTo(normalizedEmail);
      setRefusedAfterProof(false);
      setStep(STEPS.OTHER_WAY);
      return;
    }
    setErrorCode(result.code);
  };

  const handleResend = async () => {
    if (resending) return;
    setResending(true);
    setErrorCode(null);
    const result = await sendLoginCode(sentTo);
    if (!mountedRef.current) return;
    setResending(false);
    if (result.ok) {
      setChallenge(result.challenge);
    } else {
      setErrorCode(result.code);
    }
  };

  const handleVerify = async (otp) => {
    if (busy || !challenge) return;
    if (Date.now() >= challenge.expiresAt) {
      setErrorCode(INSTITUTIONAL_CODES.OTP_EXPIRED);
      return;
    }
    setBusy(true);
    setErrorCode(null);
    // Built at submit time so it reads the freshest stored QLink touch.
    const qlinkState = qlinkId
      ? buildQLinkOAuthState({ qlinkId, platform: qlinkPlatform || "unknown" })
      : undefined;
    const result = await verifyLoginCode({
      challengeId: challenge.challengeId,
      email: sentTo,
      otp,
      ...readReferralForSignup(),
      qlinkState,
    });
    if (!mountedRef.current) return;
    setBusy(false);
    if (result.ok) {
      const user = result.data;
      // Same persistence predicate every other sign-in leg uses: never store
      // a token that does not decode to a future exp.
      if (!user || !user.id || !isTokenPersistable(user.accessToken)) {
        setErrorCode(INSTITUTIONAL_CODES.BAD_RESPONSE);
        return;
      }
      if (user.isProfileComplete === false) {
        saveInstitutionalProfile(user);
        setPendingUser(user);
        onProfilePending(true);
        setStep(STEPS.PROFILE);
      } else {
        clearInstitutionalProfile();
        setStep(STEPS.SIGNING_IN);
        handOver(onSignedIn, user, sentTo);
      }
      return;
    }
    if (result.code === INSTITUTIONAL_CODES.ACCOUNT_LINK_REQUIRED) {
      setStep(STEPS.LINK_REQUIRED);
      return;
    }
    if (result.code === INSTITUTIONAL_CODES.SIGN_IN_ANOTHER_WAY) {
      setRefusedAfterProof(true);
      setStep(STEPS.OTHER_WAY);
      return;
    }
    setErrorCode(result.code);
  };

  const restartVerifiedSession = () => {
    abandonInstitutionalProfile(pendingUser);
    setPendingUser(null);
    onProfilePending(false);
    setStep(STEPS.DETAILS);
    setChallenge(null);
    setErrorCode(null);
    setFieldErrors({});
  };

  const handleProfile = async (event) => {
    event.preventDefault();
    if (busy) return;
    const first = normalizeFreeText(firstName);
    const last = normalizeFreeText(lastName);
    const name = `${first} ${last}`;
    const errors = {
      firstName: first ? null : "Enter your first name.",
      lastName: last ? null : "Enter your last name.",
      name: first && last ? validateDisplayName(name) : null,
    };
    setFieldErrors(errors);
    if (Object.values(errors).some(Boolean)) return;
    const current = getInstitutionalProfile();
    if (!current || current.accessToken !== pendingUser?.accessToken) {
      setErrorCode(INSTITUTIONAL_CODES.UNAUTHORIZED);
      return;
    }
    setBusy(true);
    setErrorCode(null);
    const result = await completeInstitutionalProfile(name);
    if (!mountedRef.current) return;
    setBusy(false);
    // A different tab may have changed the account while this request ran.
    if (getInstitutionalProfile()?.accessToken !== current.accessToken) {
      setErrorCode(INSTITUTIONAL_CODES.UNAUTHORIZED);
      return;
    }
    if (!result.ok || result.data?.isProfileComplete !== true) {
      setErrorCode(result.ok ? INSTITUTIONAL_CODES.BAD_RESPONSE : result.code);
      return;
    }
    clearInstitutionalProfile();
    onProfilePending(false);
    setStep(STEPS.SIGNING_IN);
    // A pending profile belongs to an account this door just created, so its email is
    // the address that was proven.
    handOver(onSignedIn, {...current, displayName: result.data.displayName || name, isProfileComplete: true},
      current.email);
  };

  if (step === STEPS.PROFILE) {
    return (
      <Box component="form" noValidate onSubmit={handleProfile} className="institutional-panel" data-testid="institutional-profile-step">
        <Typography variant="h6" component="h2">What should we call you?</Typography>
        <Typography variant="body2">Your email is verified. Add your name to finish creating your account.</Typography>
        <TextField autoFocus id="institutional-first-name" label="First name" value={firstName}
          onChange={(e) => setFirstName(e.target.value)} error={!!fieldErrors.firstName}
          helperText={fieldErrors.firstName || " "} inputProps={{autoComplete: "given-name"}}
          disabled={busy} fullWidth margin="dense" variant="outlined" />
        <TextField id="institutional-last-name" label="Last name" value={lastName}
          onChange={(e) => setLastName(e.target.value)} error={!!fieldErrors.lastName || !!fieldErrors.name}
          helperText={fieldErrors.lastName || fieldErrors.name || " "} inputProps={{autoComplete: "family-name"}}
          disabled={busy} fullWidth margin="dense" variant="outlined" />
        {errorMessage && <Typography role="alert" className="institutional-error" data-testid="institutional-error" {...REPLAY_MASK}>
          {errorMessage} If your name was already saved, verify your email again to continue.
        </Typography>}
        <Box className="institutional-actions">
          <Button type="submit" variant="contained" color="primary" disabled={busy}>
            {busy ? <CircularProgress size={20} color="inherit" /> : "Start learning"}
          </Button>
          <Button onClick={restartVerifiedSession} disabled={busy}>
            {errorCode ? "Verify email again" : "Use a different email"}
          </Button>
        </Box>
      </Box>
    );
  }

  const startOver = () => {
    setStep(STEPS.DETAILS);
    setChallenge(null);
    setErrorCode(null);
    setBusy(false);
  };

  if (step === STEPS.SIGNING_IN) {
    return (
      <Box className="institutional-panel" data-testid="institutional-signing-in" role="status">
        <CircularProgress size={24} />
        <Typography variant="body2">Email verified — signing you in…</Typography>
      </Box>
    );
  }

  if (step === STEPS.LINK_REQUIRED) {
    return (
      <Box className="institutional-panel" data-testid="institutional-link-required">
        <Typography variant="h6" component="h2">You already have a QLearn account</Typography>
        <Typography variant="body2">
          You verified <strong {...REPLAY_MASK}>{sentTo}</strong>, and it matches an existing QLearn account that hasn&apos;t
          linked this institutional email yet. We haven&apos;t created a new account or changed your existing one.
        </Typography>
        <Typography variant="body2">
          Sign in the way you usually do — with Google or your phone — then go to <strong>Settings → Institutional
          email</strong> to link <span {...REPLAY_MASK}>{sentTo}</span>. Your quiz history and subscription stay with your existing account.
        </Typography>
        <Box className="institutional-actions">
          {onUseGoogle && (
            <Button variant="contained" color="primary" onClick={onUseGoogle} data-testid="institutional-link-google">
              Sign in with Google
            </Button>
          )}
          {onUsePhone && (
            <Button variant="outlined" color="primary" onClick={onUsePhone} data-testid="institutional-link-phone">
              Sign in with phone
            </Button>
          )}
          <Button variant="text" onClick={startOver} data-testid="institutional-start-over">
            Use a different email
          </Button>
        </Box>
      </Box>
    );
  }

  if (step === STEPS.OTHER_WAY) {
    return (
      <Box className="institutional-panel" data-testid="email-code-sign-in-another-way">
        <Typography variant="h6" component="h2">Sign in the way you usually do</Typography>
        {refusedAfterProof ? (
          <>
            <Typography variant="body2">
              You verified <strong {...REPLAY_MASK}>{sentTo}</strong>. It belongs to a QLearn account that signs in another way,
              so we can&apos;t sign you in with a code. We haven&apos;t created a new account or changed your
              existing one.
            </Typography>
            <Typography variant="body2">
              Sign in with Google or your phone number instead. If neither works, email {SUPPORT_EMAIL}.
            </Typography>
          </>
        ) : (
          <Typography variant="body2">
            <strong {...REPLAY_MASK}>{sentTo}</strong> can&apos;t be used to sign in with a code. Sign in with Google or your
            phone number instead.
          </Typography>
        )}
        <Box className="institutional-actions">
          {onUseGoogle && (
            <Button variant="contained" color="primary" onClick={onUseGoogle} data-testid="email-code-other-way-google">
              Sign in with Google
            </Button>
          )}
          {onUsePhone && (
            <Button variant="outlined" color="primary" onClick={onUsePhone} data-testid="email-code-other-way-phone">
              Sign in with phone
            </Button>
          )}
          <Button variant="text" onClick={startOver} data-testid="email-code-other-way-start-over">
            Use a different email
          </Button>
        </Box>
      </Box>
    );
  }

  if (step === STEPS.CODE) {
    return (
      <Box className="institutional-panel" data-testid="institutional-signin">
        <InstitutionalOtpCodeStep
          email={sentTo}
          challenge={challenge}
          onVerify={handleVerify}
          onResend={handleResend}
          onChangeEmail={startOver}
          busy={busy}
          resending={resending}
          errorMessage={errorMessage}
          verifyLabel="Verify email"
        />
      </Box>
    );
  }

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSend}
      className="institutional-panel"
      data-testid="institutional-signin"
    >
      {anyAddress ? (
        <Typography variant="body2" className="institutional-intro">
          Sign in or create an account with any email address. We&apos;ll email you a one-time code — no password
          or phone number needed.
        </Typography>
      ) : (
        <Typography variant="body2" className="institutional-intro">
          Students and trainees can sign in or create an account with an institutional email ending in
          <strong> .edu</strong> or <strong>.org</strong>. We&apos;ll email you a one-time code — no password or phone
          number needed.
        </Typography>
      )}
      <TextField
        id="institutional-email"
        label={anyAddress ? "Email" : "Institutional email"}
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={!!fieldErrors.email}
        helperText={fieldErrors.email || " "}
        inputProps={{ autoComplete: "email", "data-testid": "institutional-email-input" }}
        disabled={busy}
        fullWidth
        margin="dense"
        variant="outlined"
      />
      {errorMessage && (
        <Typography variant="body2" role="alert" className="institutional-error" data-testid="institutional-error" {...REPLAY_MASK}>
          {errorMessage}
        </Typography>
      )}
      <Box className="institutional-actions">
        <Button type="submit" variant="contained" color="primary" disabled={busy} data-testid="institutional-send">
          {busy ? <CircularProgress size={20} color="inherit" /> : "Email me a code"}
        </Button>
      </Box>
    </Box>
  );
};

export default InstitutionalSignIn;
